import React, { useState, useRef, useEffect } from 'react';
import TextField from '@mui/material/TextField';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import { Divider } from '@mui/material';
import CustomModal from 'components/Global/CustomModal/CustomModal';
import { useStore } from 'store/pages/forms/store';
import useClaims from 'Hooks/claims/useClaims/useClaims';
import './cart.scss';
import { GetDefaultAddress, CreateFormsOrder } from '../../../api/forms';
import { useQuery, useMutation } from '@tanstack/react-query';

function Cart(props) {
  let cart = useStore((state) => state.cart);
  const clearCart = useStore((state) => state.CLEAR_CART);
  const [position, setPosition] = useState('left');
  const [clearModal, setClearModal] = useState(false);
  const [emptyCart, setEmptyCart] = useState(true);
  const [invalidQuantity, setInvalidQuantity] = useState(false);

  const setContainerPosition = (positionString) => {
    setPosition(positionString);
  };

  useEffect(() => {
    cart.length === 0 ? setEmptyCart(true) : setEmptyCart(false);
    setContainerPosition('left');
    return () => (cart = []);
  }, [cart]);

  return (
    <Paper className="cart-checkout-area-wrapper" square>
      <div className={'cart-checkout-area ' + position}>
        <div className="cart-area-wrapper">
          <div className="header">
            <h4>Cart</h4>
          </div>
          <div className="cart-items">
            {emptyCart ? (
              <div id="empty-cart-message">Your cart is empty</div>
            ) : (
              cart.map((cartItem) => (
                <CartItem
                  key={cartItem.FormName + '-' + cartItem.Quantity}
                  formName={cartItem.FormName}
                  formNumber={cartItem.FormNumber}
                  quantity={cartItem.Quantity}
                  setInvalidQuantity={setInvalidQuantity}
                />
              ))
            )}
          </div>
          <div className="actions">
            <Button
              variant="contained"
              className="btn btn-danger"
              disabled={emptyCart}
              onClick={() => {
                setClearModal(true);
              }}
            >
              Clear Cart
            </Button>
            <Button
              variant="contained"
              className="btn btn-primary"
              disabled={emptyCart || invalidQuantity}
              onClick={() => setContainerPosition('right')}
            >
              Checkout
            </Button>
          </div>
          <CustomModal
            show={clearModal}
            onHide={() => setClearModal(false)}
            title="Clear Cart"
            body={<span>Are you sure you want to clear the cart?</span>}
            buttonOne={
              <button
                className="btn btn-success"
                onClick={() => {
                  clearCart();
                  setClearModal(false);
                }}
              >
                Clear Cart
              </button>
            }
            buttonTwo={
              <button className="btn btn-danger" onClick={() => setClearModal(false)}>
                Cancel
              </button>
            }
            buttonThree={null}
          />
        </div>
        <div className="checkout-area-wrapper">
          <Checkout setContainerPosition={setContainerPosition} cart={cart} />
        </div>
      </div>
    </Paper>
  );
}

function CartItem(props) {
  const quantityInput = useRef(null);
  const [previousValue, setPreviousValue] = useState(undefined);
  const [invalidQuantity, setInvalidQuantity] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [quantityHelperText, setQuantityHelperText] = useState('');
  const setStoreQuantity = useStore((state) => state.SET_QUANTITY);
  const deleteCartItem = useStore((state) => state.REMOVE_ITEM);

  useEffect(() => {
    props.setInvalidQuantity(invalidQuantity);
  }, [invalidQuantity]);

  const validateQuantity = (quantityInputVal) => {
    if (parseFloat(quantityInputVal) % 1 != 0) {
      setInvalidQuantity(true);
      setQuantityHelperText('Input must be whole number');
    } else if (parseFloat(quantityInputVal) == 0) {
      setDeleteModal(true);
    } else if (parseFloat(quantityInputVal) < 0) {
      setInvalidQuantity(true);
      setQuantityHelperText('Input cannot be less than 0');
    } else {
      setInvalidQuantity(false);
      setQuantityHelperText('');
      setStoreQuantity(props.formNumber, quantityInputVal);
    }
  };
  const handleDeleteModalClose = () => {
    setDeleteModal(false);
    quantityInput.current.value == 0 ? (quantityInput.current.value = previousValue) : '';
  };

  useEffect(() => {}, [props.quantity]);

  return (
    <div className="cart-item">
      <div className="headings">
        <h5 className="heading">Form Name: {props.formName.replace('.pdf', '')}</h5>
        <h6 className="sub-heading">Form Number: {props.formNumber}</h6>
      </div>
      <div className="item-actions">
        <div className="quantity">
          <TextField
            id="quantity-text-field"
            label="Quantity"
            defaultValue={props.quantity}
            type="number"
            error={invalidQuantity}
            helperText={quantityHelperText}
            inputRef={quantityInput}
            onBlur={() => validateQuantity(quantityInput.current.value)}
            onClick={() => setPreviousValue(quantityInput.current.value)}
          />
        </div>
        <div>
          <div
            className="delete-action"
            onClick={() => {
              setDeleteModal(true);
            }}
          >
            Delete
          </div>
        </div>
      </div>
      <Divider />
      <CustomModal
        show={deleteModal}
        onHide={() => handleDeleteModalClose()}
        title="Delete Form"
        body={
          <span>Are you sure you want to delete form {props.formName.replace('.pdf', '')}</span>
        }
        buttonOne={
          <button
            className="btn btn-success"
            onClick={() => {
              deleteCartItem(props.formNumber);
              setDeleteModal(false);
            }}
          >
            Delete Form
          </button>
        }
        buttonTwo={
          <button className="btn btn-danger" onClick={() => handleDeleteModalClose()}>
            Cancel
          </button>
        }
        buttonThree={null}
      />
    </div>
  );
}

function Checkout(props) {
  const clearCart = useStore((state) => state.CLEAR_CART);
  const createFormsOrder = useMutation({
    mutationFn: async (data) => await CreateFormsOrder(data)(),
  });

  const [address, setAddress] = useState([]);
  const [name, setName] = useState('');
  const [submitModal, setSubmitModal] = useState(false);
  const checkout = useStore((state) => state.CHECKOUT);
  const [submissionInProgress, setSubmissionInProgress] = useState(false);
  const [validAddress, setValidAddress] = useState(true);
  const [submissionStatus, setSubmissionStatus] = useState('loading');

  const handleSubmission = () => {
    setSubmissionInProgress(true);

    const body = {
      Forms: props.cart,
      Address1: address.Address1,
      Address2: address.Address2,
      City: address.City,
      State: address.State,
      Zip: address.Zip,
      SendTo: name,
      UserID: 0,
    };

    createFormsOrder.mutate(body, {
      onSuccess: () => {
        clearCart();
        submissionCallback();
      },
    });
  };

  const submissionCallback = () => {
    setSubmissionInProgress(false);
    props.setContainerPosition('left');
  };
  const addressCallback = (addressVal) => {
    setAddress(addressVal);
  };
  const nameCallback = (nameVal) => {
    setName(nameVal);
  };
  const validateAddressCallback = (val) => {
    setValidAddress(val);
  };

  return (
    <>
      <div className="header">
        <h4>Address Confirmation</h4>
      </div>
      <div className="checkout">
        {submissionInProgress ? (
          <Submission submissionComplete={submissionCallback} status={submissionStatus} />
        ) : (
          <AddressConfirmation
            addressCallback={addressCallback}
            nameCallback={nameCallback}
            validateAddress={validateAddressCallback}
          />
        )}
      </div>
      <div className="actions">
        <Button
          variant="contained"
          className="btn btn-primary"
          onClick={() => props.setContainerPosition('left')}
        >
          Cart
        </Button>
        <Button
          variant="contained"
          className="btn btn-success"
          disabled={!validAddress}
          onClick={() => setSubmitModal(true)}
        >
          Submit Order
        </Button>
        <CustomModal
          show={submitModal}
          onHide={() => setSubmitModal(false)}
          title="Submit Order"
          body={<span>Are you sure you want to submit your forms order?</span>}
          buttonOne={
            <button
              type="button"
              className="btn btn-success"
              onClick={() => {
                handleSubmission();
                setSubmitModal(false);
              }}
            >
              Submit
            </button>
          }
          buttonTwo={
            <button type="button" className="btn btn-danger" onClick={() => setSubmitModal(false)}>
              Cancel
            </button>
          }
          buttonThree={null}
        />
      </div>
    </>
  );
}

function AddressConfirmation(props) {
  const [defaultAddress, setDefaultAddress] = useState();
  const [loading, setLoading] = useState(true);
  const nameRef = useRef();
  const addr1Ref = useRef();
  const addr2Ref = useRef();
  const cityRef = useRef();
  const stateRef = useRef();
  const zipRef = useRef();
  const claims = useClaims([{ ClaimName: 'Name', ClaimType: 'Value' }]);

  const getDefaultAddress = useQuery({
    queryKey: ['getDefaultAddress', 0],
    queryFn: async () => await GetDefaultAddress()(),
  });

  /*const getDefaultAddress = () => {
    const options = {
      crossDomain: true,
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      }
    };

    fetch(
      '/Forms/GetDefaultAddress',
      options
    )
      .then((res) => res.json())
      .then((data) => {
        setDefaultAddress(JSON.parse(data));
        setLoading(false);
      });
      };*/

  const handleChange = () => {
    if (
      defaultAddress.Address1 !== addr1Ref.current.value ||
      defaultAddress.Address2 !== addr2Ref.current.value ||
      defaultAddress.City !== cityRef.current.value ||
      defaultAddress.State !== stateRef.current.value ||
      defaultAddress.Zip !== zipRef.current.value
    ) {
      let address = {
        Address1: addr1Ref.current.value,
        Address2: addr2Ref.current.value,
        City: cityRef.current.value,
        State: stateRef.current.value,
        Zip: zipRef.current.value,
      };
      props.addressCallback(address);
    }
  };

  const handleNameChange = () => {
    props.nameCallback(nameRef.current.value);
  };

  /*useEffect(() => {
    getDefaultAddress();
  }, []);*/

  useEffect(() => {
    if (getDefaultAddress.isSuccess && getDefaultAddress.data) {
      setDefaultAddress(JSON.parse(getDefaultAddress.data));
      setLoading(false);
    }
  }, [getDefaultAddress.isSuccess, getDefaultAddress.data]);

  useEffect(() => {
    if (claims) {
      props.nameCallback(claims[0].Value);
    }
  }, [claims]);

  useEffect(() => {
    if (defaultAddress) {
      props.addressCallback(defaultAddress);
    }
  }, [defaultAddress]);

  return (
    <>
      {loading || !claims ? (
        <>loading...</>
      ) : (
        <form>
          <TextField
            id="name-text-field"
            className="address-item"
            label="Name"
            fullWidth
            defaultValue={claims[0].Value}
            inputRef={nameRef}
            onBlur={() => handleNameChange()}
          />
          <TextField
            id="addr1-text-field"
            className="address-item"
            label="Address 1"
            fullWidth
            defaultValue={defaultAddress.Address1}
            inputRef={addr1Ref}
            onBlur={() => handleChange()}
          />
          <TextField
            id="addr2-text-field"
            className="address-item"
            label="Address 2"
            fullWidth
            defaultValue={defaultAddress.Address2}
            inputRef={addr2Ref}
            onBlur={() => handleChange()}
          />
          <TextField
            id="city-text-field"
            className="address-item"
            label="City"
            fullWidth
            defaultValue={defaultAddress.City}
            inputRef={cityRef}
            onBlur={() => handleChange()}
          />
          <TextField
            id="state-text-field"
            className="address-item"
            label="State"
            fullWidth
            defaultValue={defaultAddress.State}
            inputRef={stateRef}
            onBlur={() => handleChange()}
          />
          <TextField
            id="zip-text-field"
            className="address-item"
            label="Zip Code"
            fullWidth
            defaultValue={defaultAddress.Zip}
            inputRef={zipRef}
            onBlur={() => handleChange()}
          />
        </form>
      )}
    </>
  );
}

function Submission(props) {
  useEffect(() => {
    console.log(props.status);
    if (props.status == 'success' || props.status == 'failure') {
      setTimeout(() => props.submissionComplete(), 3000);
    }
  }, [props.status]);

  return (
    <>
      {props.status === 'success' && <>success...</>}
      {props.status === 'failure' && <>failure...</>}
      {props.status === 'loading' && <>Submission in progress...</>}
    </>
  );

  {
    /*To do: add spinners and success icons*/
  }
}

export { Cart };
